import styled from '@emotion/styled'
import { tokens } from '@news-mono/design-tokens'
import {
    calcRem,
    breakpointMax,
    themedValue,
    fluidTypography,
    Theme,
    colors,
    breakpoint,
} from '../../../../__styling'
import { RibbonOptions } from '../../helpers/BreachWallTypes'
import { PackageThemeProps } from './SubscriptionPackage.styled'

// ------------------------------------- Single Price Point Components Below -------------------------------------

export const StyledSubscriptionOriginalPrice = styled('h4')<PackageThemeProps>(
    ({ theme, breachOptions }) => [
        {
            fontSize: calcRem(16),
            fontWeight: 700,
            lineHeight: 1.167,
            textDecoration: 'line-through',
            textTransform: 'uppercase',
            margin: 0,
            flex: '0 0 auto', // fixes ie11 column wrap bug

            fontFamily: theme.fonts.sansSerifCond,
            color:
                breachOptions.isRegional && breachOptions.useBreachDealStyle
                    ? tokens.thewest.colors.palette.westblue
                    : theme.sectionValues.primaryColor,
            fontFeatureSettings: "'pnum' on, 'lnum' on",

            [breakpointMax('xs')]: {
                fontSize: calcRem(16),
            },
        },
        // PROMO: EOFY or AFL
        (breachOptions.style === 'eofy' ||
            breachOptions.style === 'afl50-style') && {
            textTransform: 'none',
            fontFamily: theme.fonts.serif,
            color: tokens.thewest.colors.text.secondary,
        },
        // PROMO: Black Friday
        breachOptions.style === 'black-friday' && {
            fontSize: calcRem(22),
            textTransform: 'none',
        },
    ],
)

const themedPriceFluid = (theme: Theme) =>
    themedValue(theme, {
        sevennews: fluidTypography(375, 1000, 24, 28),
        perthnow: fluidTypography(375, 1020, 24, 30),
        thewest: fluidTypography(375, 1220, 24, 24),
        fallback: undefined,
    })

export const StyledSubscriptionPrice = styled('p')<PackageThemeProps>(
    ({ theme, packageOptions, breachOptions }) => [
        {
            ...themedPriceFluid(theme),
            lineHeight: 1.167,
            margin: 0,
            flex: '0 0 auto', // fixes ie11 column wrap bug
            fontSize: calcRem(32),
            fontWeight: 700,
            fontFamily: theme.fonts.sansSerifCond,
            fontFeatureSettings: "'pnum' on, 'lnum' on",
            textTransform: 'uppercase',
            color:
                breachOptions.isRegional && breachOptions.useBreachDealStyle
                    ? tokens.thewest.colors.palette.westblue
                    : theme.sectionValues.primaryColor,
        },
        // PROMO: Black Friday
        breachOptions.style === 'black-friday' && {
            fontSize: calcRem(40),
            textTransform: 'none',
        },
        // THEME: optimise-blue
        packageOptions.theme === 'optimise-blue' && {
            fontFamily: theme.fonts.body,
            color: tokens.thewest.colors.palette.black,
            textTransform: 'none',
            fontWeight: 500,
        },
    ],
)

export const StyledSubscriptionPriceQualifier = styled('p')<PackageThemeProps>(
    ({ theme, packageOptions, breachOptions }) => [
        {
            fontSize: calcRem(14),
            flex: '0 0 auto', // fixes ie11 column wrap bug
            fontWeight: 700,
            lineHeight: 1.167,
            margin: 0,
            color:
                packageOptions.theme === 'red'
                    ? colors.thewest.ladybird
                    : packageOptions.theme === 'yellow'
                    ? colors.thewest.sunshineYellow
                    : undefined,
        },
        // THEME: optimise-blue
        packageOptions.theme === 'optimise-blue' && {
            fontWeight: 400,
            margin: calcRem(theme.margins.sm, 0, 0, 0),
        },
        // THEME: vanishing-cousins, black-friday, west-blue, optimise-blue
        (packageOptions.theme === 'vanishing-cousins' ||
            breachOptions.style === 'black-friday' ||
            packageOptions.theme === 'west-blue' ||
            packageOptions.theme === 'optimise-blue') && {
            color: colors.black,
        },
        // PROMO: EOFY, AFL or Black Friday
        (breachOptions.style === 'eofy' ||
            breachOptions.style === 'afl50-style' ||
            breachOptions.style === 'black-friday') && {
            fontSize: calcRem(16),
        },
        // PROMO: EOFY
        breachOptions.style === 'eofy' && {
            marginTop: calcRem(16),
            marginBottom:
                packageOptions.type === 'digital' ? calcRem(16) : undefined,
        },
        // PROMO: AFL
        breachOptions.style === 'afl50-style' && {
            color: tokens.thewest.colors.text.secondary,
        },
    ],
)

// ------------------------------------- Radio Price Point Components Below -------------------------------------

export const StyledSubscriptionRadioContainer = styled('div')(() => ({
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    gap: calcRem(16),
    minHeight: 'auto',

    [breakpoint('xs')]: {
        minHeight: calcRem(28),
    },
}))

export const StyledSubscriptionRadioRow = styled('div')(() => ({
    display: 'flex',
    flexDirection: 'row',
    gap: calcRem(6),
}))

export const StyledSubscriptionRadioPriceContainer = styled('div')(() => ({
    display: 'flex',
    flexDirection: 'row',
    gap: calcRem(6),
    width: 'fit-content',
    height: 'fit-content',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',

    [breakpoint('xs')]: {
        gap: calcRem(8),
    },
}))
export const StyledSubscriptionRadioPrice = styled('p')(({ theme }) => ({
    color: '#2B2D2E',

    padding: 0,
    margin: 0,

    fontFamily: theme.fonts.sansSerif,
    fontWeight: 500,
    fontSize: calcRem(16),
    lineHeight: calcRem(18),

    [breakpoint('xs')]: {
        fontSize: calcRem(18),
        lineHeight: calcRem(20),
    },
}))

export const StyledSubscriptionRadioQualifier = styled('p')(({ theme }) => ({
    color: '#535556',

    padding: 0,
    margin: 0,
    paddingTop: calcRem(2),

    display: 'flex',
    justifyContent: 'center',
    textAlign: 'center',

    fontFamily: theme.fonts.sansSerif,
    fontWeight: 400,
    fontSize: calcRem(14),
    lineHeight: calcRem(16),

    [breakpoint('xs')]: {
        fontSize: calcRem(13),
        lineHeight: calcRem(14),
    },
}))

export const StyledSubscriptionRadioRibbon = styled('p')<{
    ribbonOptions: RibbonOptions
}>(({ theme, ribbonOptions }) => [
    {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: calcRem(4, 8),
        margin: 0,

        fontFamily: theme.fonts.sansSerif,
        fontSize: calcRem(12),
        fontWeight: 500,
        lineHeight: calcRem(16),
    },
    // RIBBON OPTION: Blue Pill
    ribbonOptions === 'bluePill' && {
        borderRadius: calcRem(24),
        backgroundColor: colors.thewest.westblue,
        color: colors.white,
    },
])
